<script setup lang="ts">
import { type PropType, computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  type ThemeDTO,
  THEME_TYPE_COLOR,
  THEME_TYPE_COLOR_GRADIENT,
  THEME_TYPE_PIC,
  THEME_TYPE_VIDEO
} from '@/composables/memorize/memorize'
import { debounce } from 'perfect-debounce'
import { isInWechat } from '@/utils/util'

const MIDDLE_W = 500

const props = defineProps({
  theme: {
    type: Object as PropType<ThemeDTO | undefined>,
    required: true
  }
})

const bgRef = ref<HTMLElement>()
const innerWidth = ref(MIDDLE_W + 100)

onMounted(() => {
  window.addEventListener('resize', debounced)
  innerWidth.value = window.innerWidth // 初始化时调用一次，以获取当前窗口大小
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', debounced)
})

const debounced = debounce(() => {
  innerWidth.value = window.innerWidth
}, 500)

const video = ref<HTMLVideoElement | undefined>()

const themeComputed = computed(() => {
  console.log('=== background:', props.theme)
  return props.theme
})

watch(
  () => props.theme,
  (newValue) => {
    console.log('=== watch:', newValue)
    if (video.value && newValue?.themeType == THEME_TYPE_VIDEO && newValue?.file) {
      video.value.src = newValue.file.url
    }
  }
)

function calGradientColor(colorValue: string | undefined) {
  if (colorValue) {
    const colors = colorValue.split('_')
    const startColor = colors[0]
    const endColor = colors[1]
    return {
      background: `linear-gradient(90deg, ${startColor}, ${endColor})`
    }
  } else {
    return { background: '#fff' }
  }
}

const calcImageBackground = computed(() => {
  if (themeComputed.value?.themeType == THEME_TYPE_PIC) {
    if (themeComputed.value?.file) {
      if (themeComputed.value?.smallFile == null) {
        return {
          backgroundImage: `url(${themeComputed.value?.file?.highQuality || ''})` /* 设置背景图片 */,
          backgroundSize: 'cover' /* 背景图片覆盖整个元素区域 */,
          backgroundPosition: 'center' /* 背景图片居中显示 */,
          backgroundRepeat: 'no-repeat' /* 背景图片不重复 */
        }
      } else {
        return {
          backgroundImage: `url(${
            innerWidth.value > MIDDLE_W
              ? themeComputed.value?.file?.highQuality
              : themeComputed.value?.smallFile?.highQuality
          })` /* 设置背景图片 */,
          backgroundSize: 'cover' /* 背景图片覆盖整个元素区域 */,
          backgroundPosition: 'center' /* 背景图片居中显示 */,
          backgroundRepeat: 'no-repeat' /* 背景图片不重复 */
        }
      }
    } else {
      return { background: '#fff' }
    }
  } else {
    return {
      backgroundImage: `url(${
        innerWidth.value > MIDDLE_W ? themeComputed.value?.gifFile?.url : themeComputed.value?.gifSmallFile?.url
      })` /* 设置背景图片 */,
      backgroundSize: 'cover' /* 背景图片覆盖整个元素区域 */,
      backgroundPosition: 'center' /* 背景图片居中显示 */,
      backgroundRepeat: 'no-repeat' /* 背景图片不重复 */
    }
  }
})

const calcVideoSrc = computed(() => {
  if (themeComputed.value?.file) {
    if (themeComputed.value?.smallFile === null) {
      return themeComputed.value?.file?.url
    } else {
      return innerWidth.value > MIDDLE_W ? themeComputed.value?.file?.url : themeComputed.value?.smallFile?.url
    }
  }
  return ''
})
</script>
<template>
  <div ref="bgRef" h-full w-full :style="{ backgroundColor: themeComputed?.colorValue }">
    <div
      v-if="themeComputed?.themeType == THEME_TYPE_COLOR"
      h-full
      w-full
      :style="{ backgroundColor: themeComputed.colorValue || '#fff' }"></div>
    <div
      v-if="themeComputed && themeComputed.themeType == THEME_TYPE_COLOR_GRADIENT"
      h-full
      w-full
      :style="calGradientColor(themeComputed.colorValue)"></div>
    <div
      v-if="
        (themeComputed && themeComputed.themeType === THEME_TYPE_PIC) ||
        (themeComputed && themeComputed.themeType == THEME_TYPE_VIDEO && isInWechat())
      "
      h-full
      w-full
      :style="calcImageBackground"></div>
    <video
      v-if="themeComputed && themeComputed.themeType === THEME_TYPE_VIDEO && !isInWechat()"
      ref="video"
      preload="none"
      :poster="themeComputed?.gifFile?.url"
      :controls="false"
      muted
      loop
      playsinline
      fullscreen:object-contain
      autoplay
      h-full
      w-full
      object-cover>
      <source v-if="calcVideoSrc" :src="calcVideoSrc" type="video/mp4" />
    </video>
  </div>
</template>
<style lang="scss" scoped></style>
